import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  Users,
  Calendar,
  Plane,
  Settings,
  Wrench,
  UserCheck,
  GraduationCap,
  ClipboardCheck,
  Clock,
  Shield,
  BarChart3,
  TrendingUp,
  MessageSquare,
} from 'lucide-react';
import { openFeedbackMail } from '../FeedbackButton';
import { WhatsNewButton } from '../whatsNew/WhatsNewButton';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission } from '../../core/permissions';
import { useLanguage } from '../../contexts/LanguageContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOpenSquawkCount } from '../../hooks/useOpenSquawkCount';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  permission?: Permission;
  anyPermission?: Permission[];
  flightSchoolOnly?: boolean;
  organizationTypes?: ('club' | 'school')[];
}

export const Sidebar: React.FC = () => {
  const { hasPermission, isFlightSchool } = usePermissions();
  const { t } = useLanguage();
  const { organization, error: orgError } = useOrganization();
  const { loading: authLoading, currentUser } = useAuth();
  // The open-squawk badge is a triage signal for admins/maintenance only. Members
  // hold MAINTENANCE:READ (to file and view their *own* squawks) but must not see
  // the org-wide open count — gate the badge on MAINTENANCE:UPDATE_SQUAWK, matching
  // the maintenance list page's `canSeeAllReports` gate. Passing undefined when the
  // viewer can't triage means their client never even runs the org-wide count query.
  const canTriageSquawks = hasPermission('MAINTENANCE:UPDATE_SQUAWK');
  const openSquawks = useOpenSquawkCount(canTriageSquawks ? organization?.id : undefined);

  // Until auth + the organization are resolved, hasPermission() can only return
  // false (it needs both the role and the org type), so the nav would render just
  // the two unguarded items (Dashboard, Settings). That happens right after login,
  // when the shell mounts before the org fetch finishes — the user previously had
  // to refresh to get the full menu (OMN-318). Spin only until the org first
  // resolves; once it's loaded, a non-null `organization` keeps this false, so
  // background refetches (which keep the org on screen) never flash the nav. The
  // org-error / orphaned-user (no orgId) cases fall through to the limited nav
  // rather than spin forever.
  const permissionsResolving =
    authLoading || (!!currentUser?.orgId && !organization && !orgError);
  if (permissionsResolving) {
    return (
      <div className="w-64 bg-white shadow-lg h-full flex flex-col">
        <nav className="flex-1 py-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
        </nav>
      </div>
    );
  }

  const allNavigationItems: NavigationItem[] = [
    { name: t('navigation.dashboard'), href: '/dashboard', icon: Home },
    { name: t('navigation.members'), href: '/members', icon: Users, permission: 'MEMBERS:READ' },
    { name: t('navigation.bookings'), href: '/bookings', icon: Calendar, permission: 'BOOKINGS:READ' },
    { name: t('navigation.aircrafts'), href: '/aircrafts', icon: Plane, permission: 'AIRCRAFT:READ' },
    { name: t('navigation.maintenance'), href: '/maintenance/reports', icon: Wrench, permission: 'MAINTENANCE:READ' },

    // Flight school specific items
    { name: t('navigation.availability'), href: '/availability', icon: Clock, anyPermission: ['AVAILABILITY:READ_OWN', 'AVAILABILITY:READ_ALL'], flightSchoolOnly: true },
    { name: t('navigation.studentAssignments'), href: '/assignments', icon: UserCheck, permission: 'ASSIGNMENTS:READ', flightSchoolOnly: true },
    { name: t('navigation.trainingProgress'), href: '/training', icon: GraduationCap, permission: 'TRAINING:READ', flightSchoolOnly: true },
    { name: t('navigation.dispatchCenter'), href: '/dispatch', icon: ClipboardCheck, permission: 'BOOKINGS:DISPATCH_OVERRIDE', flightSchoolOnly: true },

    { name: t('navigation.settings'), href: '/settings', icon: Settings },
    { name: t('navigation.insights'), href: '/admin/insights', icon: TrendingUp, permission: 'ORG:VIEW_ANALYTICS' },
    { name: t('navigation.reports'), href: '/admin/reports', icon: BarChart3, permission: 'ORG:VIEW_ANALYTICS' },
    { name: 'Audit Log', href: '/admin/audit-log', icon: Shield, permission: 'ORG:MANAGE_SETTINGS' },
  ];

  // Filter navigation items based on permissions and organization type
  const navigation = allNavigationItems.filter(item => {
    if (item.flightSchoolOnly && !isFlightSchool) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.anyPermission && !item.anyPermission.some(p => hasPermission(p))) return false;
    return true;
  });

  return (
    <div className="w-64 bg-white shadow-lg h-full flex flex-col">
      <nav className="flex-1 py-6">
        {navigation.map((item) => {
          // Open-squawk badge on the Maintenance item; the link carries the
          // open filter so clicking lands on the open reports (OMN-284).
          const isMaintenance = item.href === '/maintenance/reports';
          const showBadge = isMaintenance && openSquawks > 0;
          const to = showBadge ? '/maintenance/reports?status=open' : item.href;
          return (
            <NavLink
              key={item.name}
              to={to}
              className={({ isActive }) =>
                `flex items-center px-6 py-3 transition-colors duration-150 text-gray-600 hover:bg-gray-50 hover:text-gray-900
                ${isActive ? 'bg-blue-100 text-blue-900 font-bold border-l-4 border-blue-600' : ''}`
              }
            >
              <item.icon className="h-5 w-5 mr-3" />
              <span className="flex-1">{item.name}</span>
              {showBadge && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-red-600 rounded-full">
                  {openSquawks > 99 ? '99+' : openSquawks}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Feedback — mobile only; on desktop the floating button handles this. */}
      <button
        type="button"
        onClick={openFeedbackMail}
        className="lg:hidden flex items-center px-6 py-3 text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors duration-150 border-t border-gray-100"
      >
        <MessageSquare className="h-5 w-5 mr-3" />
        <span className="flex-1 text-left">Feedback</span>
      </button>

      {/* What's new — visible on desktop (bottom of sidebar) and mobile drawer
          (just below Feedback). Opens the changelog slide-over (OMN-368). */}
      <WhatsNewButton />
    </div>
  );
}; 